import {useTranslation} from "react-i18next";
import {useEffect, useState, useRef, useMemo} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import axiApi from "ApiCaller";
import navs from "../navs";
import DateUtils from "core/utils/DateUtils";
import NavBreadcrumb from "components/NavBreadcrum";
import MapAssetAndDevicesJS from "components/maps/MapAssetAndDevicesJS";
import ConditionalRender from "components/ConditionalRender";
import LineEChartManager from "../components/LineEchartManager";
import LineEChartMultipleY from "../components/LineEChartMultipleY";
import {Asset} from "site-apps/assets/AssetModels";
import {AlertsTypes} from "site-apps/alerts/AlertsTypesCharts";
import AlertsUtils from "./AlertsUtils";
import ParamsAsset from "./waste/ParamsAsset";
import NavAssetArrows from "./NavAssetArrows";
import useCustomTranslation from "core/customTranslation";
import {translations} from "./translations";

const AlertsModel = function () {
    return {
        measures: {},
        battery: {},
        temperature: {},
    };
};

const FormDataContainer = () => {
    const {t: _Tr} = useTranslation("routes");
    const {t: _Tg} = useTranslation("grafics");
    const {t: _tC} = useCustomTranslation(translations);
    const title = _Tr("labels.container");

    const {uid: uidParam} = useParams();
    const [uid, setUid] = useState(uidParam);
    const [asset, setAsset] = useState(Asset);
    const [measures, setMeasures] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    // deviceCoords will be an array of {uuid, lat, lng}
    const [deviceCoords, setDeviceCoords] = useState([]);
    const [navInfo, setNavInfo] = useState({position: null, total: null, back: null, next: null});

    const navigate = useNavigate();

    const handleNavigate = (targetUid) => {
        if (!targetUid || targetUid === uid) return;
        setUid(targetUid);
        navigate(`/data-view/data-container/${targetUid}/`);
    };

    const bread = useMemo(
        () => [
            {name: _Tr("labels.data-view"), url: "/data-view/", current: false},
            {
                name: _Tr("labels.containers"),
                url: "/data-view/containers/",
                current: false,
            },
            {name: title, current: true},
        ],
        [_Tr],
    );

    const chartRefs = useRef({
        filling: null,
        battery: null,
        temperature: null,
    });

    useEffect(() => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);
        setDateRange({startDate, endDate});
        return () => {
            sessionStorage.removeItem("viewsDataContainersState");
        };
    }, [uid]);

    useEffect(() => {
        if (dateRange) {
            const savedState = JSON.parse(sessionStorage.getItem("viewsDataContainersState") || "{}");
            let dataSend = {
                uid: uid,
                start: DateUtils.dateToStringInput(dateRange.startDate),
                end: DateUtils.dateToStringInput(dateRange.endDate),
                filters: savedState.filters || {},
                ordering: savedState.ordering || "",
            };
            axiApi.post(`/api/assets/asset/data/uid/`, dataSend).then((resp) => {
                setAsset(resp.data.item);
                setNavInfo({
                    position: resp.data.navigate?.position ?? null,
                    total: resp.data.navigate?.total ?? null,
                    back: resp.data.item?.back ?? null,
                    next: resp.data.item?.next ?? null,
                });
                let measurs = {...resp.data.measures};
                let alert, device, alertSerie;

                for (device of resp.data.item.devices) {
                    if (!(device.uuid in measurs)) {
                        measurs[device.uuid] = {};
                    }
                    if (!("alerts" in measurs[device.uuid])) {
                        measurs[device.uuid]["alerts"] = new AlertsModel();
                    }
                }
                setDeviceCoords([...resp.data.item.devices]);
                for (alert of resp.data.alerts) {
                    if (!(alert.uuid in measurs)) {
                        measurs[alert.uuid] = {};
                        measurs[alert.uuid]["alerts"] = new AlertsModel();
                    }
                    switch (alert.type) {
                        case AlertsTypes.LEVEL_UP:
                            alertSerie = {
                                x: alert.date * 1000,
                                y: 90,
                            };
                            break;
                        case AlertsTypes.EMPTYING_ALERT:
                        case AlertsTypes.UNLOAD:
                            alertSerie = {
                                x: alert.date * 1000,
                                y: 0,
                            };
                            break;

                        default:
                            alertSerie = {
                                x: alert.date * 1000,
                                y: 100,
                            };
                    }

                    AlertsUtils.addAlert(measurs, alert, alertSerie);
                }
                setMeasures(measurs);
            });
        }
    }, [dateRange, uid]);

    const handleResetZoom = () => {
        Object.keys(chartRefs.current).forEach((key) => {
            const chart = chartRefs.current[key];
            if (chart && chart.resetZoom) {
                chart.resetZoom();
            }
        });
    };

    return (
        <>
            <NavBreadcrumb navs={navs} bread={bread} />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 preview-canvas offcanvas-header mt-1">
                        <div className="preview-icon">
                            <FontAwesomeIcon icon="trash-can" />
                        </div>
                        <div className="d-flex flex-row justify-content-between align-items-center w-100">
                            <div className="preview-title ps-4">
                                <h5>{title}</h5>
                            </div>
                            <NavAssetArrows navInfo={navInfo} onNavigate={handleNavigate} />
                        </div>
                    </div>
                </div>
                <div className="row">
                        <div className="col-md-8 col-12">
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="d-inline-block small me-2 text-secondary">UID:</label>
                                    <span className="d-inline-block text-black h5">{asset.uid}</span>
                                    <ConditionalRender condition={navInfo.position}>
                                        <small className="text-muted ms-2">
                                            {navInfo.position}/{navInfo.total}
                                        </small>
                                    </ConditionalRender>
                                </div>
                            </div>
                        <ParamsAsset
                            item={asset}
                            handleResetZoom={handleResetZoom}
                            dateRange={dateRange}
                            setDateRange={setDateRange}
                        />
                    </div>
                    <div className="col-md-4 col-12">
                        <ConditionalRender
                            condition={asset.lat && asset.lng}
                            fallback={<div className="alert alert-secondary">{_tC("no.coordinates")}</div>}>
                            <div
                                style={{
                                    height: "300px",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                }}>
                                <MapAssetAndDevicesJS
                                    asset={asset}
                                    devices={deviceCoords}
                                    mapId="map-data-container"
                                    height="300px"
                                    zoom={13}
                                />
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 mb-5">
                        <ConditionalRender condition={measures}>
                            {Object.keys(measures).map((device, deviceIndex) => (
                                <div key={deviceIndex} className="row">
                                    <div className="col-12">
                                        <h5 className="mb-3">UUID: {device}</h5>
                                        <div className="row">
                                            <div className="col-12 mb-1">
                                                <h5 className="m-0">{_Tg("headers.containers.filling")}</h5>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div
                                                className={`col-12 ${
                                                    measures[device].filling && measures[device].filling.length > 0
                                                        ? "chart-limited"
                                                        : ""
                                                }`}>
                                                {measures[device].filling && measures[device].filling.length > 0 ? (
                                                    <LineEChartManager
                                                        measuresData={{
                                                            filling: measures[device].filling,
                                                        }}
                                                        translations={{
                                                            filling: _Tg("headers.containers.filling"),
                                                        }}
                                                        alerts={measures[device]?.alerts?.measures || {}}
                                                        chartRef={(ref) => (chartRefs.current.filling = ref)}
                                                    />
                                                ) : (
                                                    <div className="alert alert-secondary mt-2">{_tC("no.measures")}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <hr className="mb-3" />
                                        <h5 className="mb-3">UUID: {device}</h5>
                                        <h5 className="m-0">{_Tg("headers.containers.battery_one")}</h5>
                                        <div
                                            className={`col-12 ${
                                                measures[device].battery_one && measures[device].battery_one.length > 0
                                                    ? "chart-limited"
                                                    : ""
                                            }`}>
                                            {measures[device].battery_one && measures[device].battery_one.length > 0 ? (
                                                <LineEChartMultipleY
                                                    measuresData={{
                                                        battery: measures[device].battery_one || [],
                                                        voltage: measures[device].voltage || [],
                                                    }}
                                                    chartRef={(ref) => (chartRefs.current.battery = ref)}
                                                    translations={{
                                                        battery: _Tg("headers.containers.battery_one"),
                                                        voltage: _Tg("headers.containers.voltage"),
                                                    }}
                                                    alerts={measures[device]?.alerts.battery || {}}
                                                />
                                            ) : (
                                                <div className="alert alert-secondary mt-2">{_tC("no.battery")}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <hr className="mb-3" />
                                        <h5 className="mb-3">UUID: {device}</h5>
                                        <h5 className="m-0">{_Tg("headers.containers.temperature")}</h5>
                                        <div
                                            className={`col-12 p-0 ${
                                                measures[device].temperature && measures[device].temperature.length > 0
                                                    ? "chart-limited"
                                                    : ""
                                            }`}>
                                            {" "}
                                            {measures[device].temperature && measures[device].temperature.length > 0 ? (
                                                <LineEChartManager
                                                    measuresData={{
                                                        temperature: measures[device].temperature || [],
                                                    }}
                                                    translations={{
                                                        temperature: _Tg("headers.containers.temperature"),
                                                    }}
                                                    chartRef={(ref) => (chartRefs.current.temperature = ref)}
                                                    alerts={measures[device]?.alerts.temperature || {}}
                                                    min={-20}
                                                    max={100}
                                                />
                                            ) : (
                                                <div className="alert alert-secondary mt-2">{_tC("no.temperature")}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </ConditionalRender>
                    </div>
                </div>
            </div>
        </>
    );
};
export default FormDataContainer;
